// Phase 14e-b — Caregiver detail Payouts section.
//
// Per 14e-b-5 lock: between Banking + Audit trail. Renders paginated
// AdminTable of GET /admin/payouts?caregiverId={id}. Row hosts 7 action
// buttons per the 14e-4 tier matrix (issue / void / regenerate /
// record-payment / record-eft-dispatch / record-eft-rejection /
// re-render-statement). Click row → opens inline <PayoutDetailModal>
// with lines + events.

import { useRef, useState, type ReactNode } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader, Field, Input, useFocusTrap } from '@truecare/ui';
import { messageOf, unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminTable, type AdminTableColumn } from './AdminTable';
import { DangerButton } from './DangerButton';
import { OverrideActionButton } from './OverrideActionButton';

interface PayoutRow {
  id: string;
  payoutNumber: string | null;
  caregiverId: string;
  caregiverName: string;
  status: string;
  subtotalCents: number;
  hstCents: number;
  totalCents: number;
  cycleStartDate: string;
  cycleEndDate: string;
  issuedAt: string | null;
  holdUntil: string | null;
  paidAt: string | null;
  voidedAt: string | null;
}

interface PayoutDetail extends PayoutRow {
  mechanic: string | null;
  lines: {
    id: string;
    description: string;
    amountCents: number;
  }[];
  events: {
    id: string;
    eventType: string;
    payloadJson: unknown;
    recordedAt: string;
  }[];
}

export function PayoutsCard({ caregiverId }: { caregiverId: string }) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [detailId, setDetailId] = useState<string | null>(null);

  const queryKey = ['admin', 'payouts', { caregiverId, page, limit }] as const;
  const listQuery = useQuery({
    queryKey,
    queryFn: async () => {
       
      const r = (await unwrap(
        api.GET('/api/v1/admin/payouts' as never, {
          params: {
            query: {
              caregiverId,
              page: String(page),
              pageSize: String(limit),
            },
          },
        } as never),
      )) as { items: PayoutRow[]; totalCount: number };
      return r;
    },
  });

  function invalidate(): void {
    void qc.invalidateQueries({ queryKey: ['admin', 'payouts'] });
  }

  const columns: AdminTableColumn<PayoutRow>[] = [
    {
      key: 'payoutNumber',
      header: 'Number',
      width: 'w-40',
      // DRAFT payouts have no number until ISSUE. Use the em-dash (matching
      // the sibling `/payouts` list route) so a draft row doesn't render
      // "DRAFT" in both the Number and Status cells, which reads as a bug.
      render: (r) => r.payoutNumber ?? '—',
    },
    { key: 'status', header: 'Status', width: 'w-28' },
    {
      key: 'cycleStartDate',
      header: 'Cycle',
      width: 'w-44',
      render: (r) => `${r.cycleStartDate} → ${r.cycleEndDate}`,
    },
    {
      key: 'totalCents',
      header: 'Total',
      width: 'w-28',
      render: (r) => formatCents(r.totalCents),
    },
    {
      key: 'issuedAt',
      header: 'Issued',
      width: 'w-32',
      render: (r) => (r.issuedAt ? r.issuedAt.slice(0, 10) : '—'),
    },
    {
      key: 'holdUntil',
      header: 'Hold until',
      width: 'w-28',
      render: (r) => (r.holdUntil ? r.holdUntil.slice(0, 10) : '—'),
    },
    {
      key: '__actions',
      header: 'Actions',
      sortable: false,
      width: 'w-auto',
      render: (r) => (
        <PayoutRowActions
          payout={r}
          caregiverId={caregiverId}
          onMutation={invalidate}
        />
      ),
    },
  ];

  return (
    <Card padding="md" className="mt-6" data-testid="caregiver-payouts-card">
      <CardHeader title="Payouts" />
      <div className="mt-4">
        <AdminTable
          columns={columns}
          rows={listQuery.data?.items ?? []}
          isLoading={listQuery.isPending}
          isError={listQuery.isError}
          emptyMessage="No payouts for this professional yet."
          page={page}
          limit={limit}
          totalCount={listQuery.data?.totalCount ?? 0}
          onPageChange={setPage}
          onLimitChange={(n) => {
            // 2026-07-23 audit (U4): resetting to page 1 is required — the
            // backends compute skip=(page-1)*pageSize with no clamping, so
            // growing the page size while on page 2+ can put skip past
            // totalCount and render "no results" over a populated list.
            setLimit(n);
            setPage(1);
          }}
          onRowClick={(r) => setDetailId(r.id)}
        />
      </div>

      {detailId && (
        <PayoutDetailModal
          payoutId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}
    </Card>
  );
}

// ─── Row actions ────────────────────────────────────────────────────────────

function PayoutRowActions({
  payout,
  caregiverId,
  onMutation,
}: {
  payout: PayoutRow;
  caregiverId: string;
  onMutation: () => void;
}) {
  const issue = useMutation({
    mutationFn: () =>
       
      unwrap(
        api.POST('/api/v1/admin/payouts/{id}/issue' as never, {
          params: { path: { id: payout.id } },
          body: {},
        } as never),
      ),
    onSuccess: onMutation,
    // messageOf() reads the backend's real Convention-03 reason
    // (error.body.message) — e.message alone is the generic "API 4xx".
    onError: (e) => {
      window.alert(`Failed to issue payout: ${messageOf(e, 'unknown error')}`);
    },
  });
  const regenerate = useMutation({
    mutationFn: (body: { reason: string }) =>
       
      unwrap(
        api.POST('/api/v1/admin/payouts/{id}/regenerate' as never, {
          params: { path: { id: payout.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const voidMut = useMutation({
    mutationFn: (body: { reason: string }) =>
       
      unwrap(
        api.POST('/api/v1/admin/payouts/{id}/void' as never, {
          params: { path: { id: payout.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const recordPayment = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
       
      unwrap(
        api.POST('/api/v1/admin/payouts/{id}/record-payment' as never, {
          params: { path: { id: payout.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const recordEftDispatch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
       
      unwrap(
        api.POST('/api/v1/admin/payouts/{id}/record-eft-dispatch' as never, {
          params: { path: { id: payout.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const recordEftRejection = useMutation({
    mutationFn: (body: { rejectionReason: string; externalRef?: string }) =>
       
      unwrap(
        api.POST('/api/v1/admin/payouts/{id}/record-eft-rejection' as never, {
          params: { path: { id: payout.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const reRenderStatement = useMutation({
    mutationFn: () =>
       
      unwrap(
        api.POST('/api/v1/admin/payouts/{id}/re-render-statement' as never, {
          params: { path: { id: payout.id } },
          body: {},
        } as never),
      ),
    onSuccess: onMutation,
  });
  // Phase 14e-c-6 — 8th button. Force-close the cycle this payout
  // belongs to; backend creates a corrected DRAFT for the same
  // (caregiverId, cycleStartDate). Idempotent like the invoice side.
  const forceCycleClose = useMutation({
    mutationFn: (body: { caregiverId: string; cycleStartDate: string; reason: string }) =>
       
      unwrap(
        api.POST('/api/v1/admin/payouts/force-cycle-close' as never, {
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });

  const isDraft = payout.status === 'DRAFT';
  const isVoided = payout.status === 'VOIDED';
  const isIssued = !isDraft && !isVoided;

  const [paymentMechanic, setPaymentMechanic] = useState<string>('EFT');
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [dispatchRef, setDispatchRef] = useState<string>('');
  const [rejectionRef, setRejectionRef] = useState<string>('');

  return (
    <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
      {isDraft && (
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            if (window.confirm(`Issue payout ${payout.payoutNumber ?? '(DRAFT)'}?`)) {
              issue.mutate();
            }
          }}
          loading={issue.isPending}
          data-testid={`payout-issue-${payout.id}`}
        >
          Issue
        </Button>
      )}
      {isDraft && (
        <OverrideActionButton
          actionLabel="Regenerate payout"
          onConfirm={({ reason }) => regenerate.mutateAsync({ reason })}
          loading={regenerate.isPending}
          data-testid={`payout-regenerate-${payout.id}`}
        >
          Regenerate
        </OverrideActionButton>
      )}
      {!isVoided && (
        <DangerButton
          entityName={payout.payoutNumber ?? 'DRAFT'}
          actionLabel="Void payout"
          confirmationToken={payout.payoutNumber ?? 'DRAFT'}
          onConfirm={({ reason }) => voidMut.mutateAsync({ reason: reason ?? '' })}
          loading={voidMut.isPending}
          data-testid={`payout-void-${payout.id}`}
        >
          Void
        </DangerButton>
      )}
      {isIssued && (
        <OverrideActionButton
          actionLabel="Record payment"
          reasonLabel="Notes"
          instructions="Record the actual payment mechanic + bank reference once the EFT/cheque clears."
          extraFields={
            <>
              <Field label="Mechanic">
                <select
                  value={paymentMechanic}
                  onChange={(e) => setPaymentMechanic(e.target.value)}
                  className="w-full rounded-md border border-border bg-cream p-2 text-sm"
                  data-testid={`payout-payment-mechanic-${payout.id}`}
                >
                  <option value="EFT">EFT</option>
                  <option value="INTERAC">Interac</option>
                  <option value="CHEQUE_MAILED">Cheque mailed</option>
                  <option value="MANUAL">Manual</option>
                </select>
              </Field>
              <Field label="External reference (optional)">
                <Input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  data-testid={`payout-payment-ref-${payout.id}`}
                />
              </Field>
            </>
          }
          onConfirm={async ({ reason }) => {
            await recordPayment.mutateAsync({
              mechanic: paymentMechanic,
              externalRef: paymentRef.trim() || undefined,
              notes: reason,
            });
            setPaymentRef('');
          }}
          loading={recordPayment.isPending}
          data-testid={`payout-record-payment-${payout.id}`}
        >
          Record payment
        </OverrideActionButton>
      )}
      {isIssued && (
        <OverrideActionButton
          actionLabel="Record EFT dispatch"
          reasonLabel="Notes"
          instructions="Operator-opt-in audit when the EFT batch is sent to the bank. Status stays ISSUED."
          extraFields={
            <Field label="External reference">
              <Input
                type="text"
                value={dispatchRef}
                onChange={(e) => setDispatchRef(e.target.value)}
                data-testid={`payout-dispatch-ref-${payout.id}`}
              />
            </Field>
          }
          onConfirm={async ({ reason }) => {
            // externalRef is required per AdminPayoutRecordEftDispatch
            // RequestSchema — backend rejects empty string.
            if (!dispatchRef.trim()) {
              throw new Error('External reference is required.');
            }
            await recordEftDispatch.mutateAsync({
              externalRef: dispatchRef.trim(),
              notes: reason,
            });
            setDispatchRef('');
          }}
          loading={recordEftDispatch.isPending}
          data-testid={`payout-record-eft-dispatch-${payout.id}`}
        >
          Record EFT dispatch
        </OverrideActionButton>
      )}
      {isIssued && (
        <OverrideActionButton
          actionLabel="Record EFT rejection"
          reasonLabel="Rejection reason"
          instructions="Bank rejected the EFT. Payout stays ISSUED so a fallback mechanic can be recorded later via Record payment."
          extraFields={
            <Field label="External reference (optional)">
              <Input
                type="text"
                value={rejectionRef}
                onChange={(e) => setRejectionRef(e.target.value)}
                data-testid={`payout-rejection-ref-${payout.id}`}
              />
            </Field>
          }
          onConfirm={async ({ reason }) => {
            await recordEftRejection.mutateAsync({
              rejectionReason: reason,
              externalRef: rejectionRef.trim() || undefined,
            });
            setRejectionRef('');
          }}
          loading={recordEftRejection.isPending}
          data-testid={`payout-record-eft-rejection-${payout.id}`}
        >
          Record EFT rejection
        </OverrideActionButton>
      )}
      {!isDraft && !isVoided && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            if (window.confirm('Re-render statement PDF?')) {
              reRenderStatement.mutate();
            }
          }}
          loading={reRenderStatement.isPending}
          data-testid={`payout-re-render-statement-${payout.id}`}
        >
          Re-render statement
        </Button>
      )}
      {/* Phase 14e-c-6 — force-cycle-close on VOIDED rows. Post-void
       *  recovery: creates a corrected DRAFT for the same cycle. */}
      {isVoided && (
        <OverrideActionButton
          actionLabel="Force-close this cycle"
          reasonLabel="Reason"
          instructions="Creates a corrected DRAFT payout for the same cycle. Use after voiding an issued payout. Backend is idempotent: if a non-voided payout already exists for this cycle, the call is a no-op."
          onConfirm={async ({ reason }) => {
            await forceCycleClose.mutateAsync({
              caregiverId,
              cycleStartDate: payout.cycleStartDate,
              reason,
            });
          }}
          loading={forceCycleClose.isPending}
          data-testid={`payout-force-cycle-close-${payout.id}`}
        >
          Force-close cycle
        </OverrideActionButton>
      )}
    </div>
  );
}

// ─── Inline detail modal ────────────────────────────────────────────────────

function PayoutDetailModal({
  payoutId,
  onClose,
}: {
  payoutId: string;
  onClose: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ['admin', 'payouts', payoutId] as const,
    queryFn: async () => {
       
      const r = (await unwrap(
        api.GET('/api/v1/admin/payouts/{id}' as never, {
          params: { path: { id: payoutId } },
        } as never),
      )) as PayoutDetail;
      return r;
    },
  });

  // Focus in, trap Tab, Escape-to-close, restore focus on unmount (H-F4).
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { onEscape: onClose });

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="payout-detail-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="payout-detail-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <h2
            id="payout-detail-modal-title"
            className="font-heading text-xl text-forest"
          >
            Payout detail
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>

        {detailQuery.isPending && <p className="mt-4 text-sm">Loading…</p>}
        {detailQuery.isError && (
          <Alert tone="danger" className="mt-3">
            {messageOf(detailQuery.error, 'Failed to load payout detail.')}
          </Alert>
        )}
        {detailQuery.data && (
          <div className="mt-4 space-y-6">
            <Summary detail={detailQuery.data} />
            <Lines lines={detailQuery.data.lines} />
            <Events events={detailQuery.data.events} />
          </div>
        )}
      </div>
    </div>
  );
}

function Summary({ detail }: { detail: PayoutDetail }) {
  return (
    <dl className="grid grid-cols-2 gap-3 text-sm">
      <Field2 label="Number" value={detail.payoutNumber ?? 'DRAFT'} />
      <Field2 label="Status" value={detail.status} />
      <Field2 label="Cycle" value={`${detail.cycleStartDate} → ${detail.cycleEndDate}`} />
      <Field2 label="Total" value={formatCents(detail.totalCents)} />
      <Field2 label="HST" value={formatCents(detail.hstCents)} />
      <Field2 label="Mechanic" value={detail.mechanic ?? '—'} />
      <Field2 label="Issued at" value={detail.issuedAt ?? '—'} />
      <Field2 label="Hold until" value={detail.holdUntil ?? '—'} />
    </dl>
  );
}

function Field2({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="mt-1">{value}</dd>
    </div>
  );
}

function Lines({ lines }: { lines: PayoutDetail['lines'] }) {
  return (
    <section>
      <h3 className="font-heading text-sm uppercase tracking-wide text-forest mb-2">
        Lines ({lines.length})
      </h3>
      {lines.length === 0 ? (
        <p className="text-sm text-ink-muted">No lines.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-ink/5 text-left">
            <tr>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2 w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-t border-border">
                <td className="px-3 py-2">{l.description}</td>
                <td className="px-3 py-2">{formatCents(l.amountCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Events({ events }: { events: PayoutDetail['events'] }) {
  return (
    <section>
      <h3 className="font-heading text-sm uppercase tracking-wide text-forest mb-2">
        Events ({events.length})
      </h3>
      {events.length === 0 ? (
        <p className="text-sm text-ink-muted">No events yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-ink/5 text-left">
            <tr>
              <th className="px-3 py-2 w-44">Type</th>
              <th className="px-3 py-2 w-44">Recorded at</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id} className="border-t border-border">
                <td className="px-3 py-2">{e.eventType}</td>
                <td className="px-3 py-2">{e.recordedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function formatCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}$${(abs / 100).toFixed(2)}`;
}
